import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Chip, Dialog, DialogContent, DialogTitle, Grid, Typography } from '@material-ui/core';
import HourglassFullIcon from '@material-ui/icons/HourglassFull';
import { find, keyBy } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { StyledButton } from '../../../../../App/Shifamed/Components/forms/presenters/StyledListButton';
import { translate } from '../../../../../common/intl';
import { authSelectors } from '../../../../../state/ducks/auth';
import { Group } from '../../../../../state/ducks/auth/types';
import { companyActions, companySelectors } from '../../../../../state/ducks/company';
import { Employee } from '../../../../../state/ducks/company/types';
import { documentTypeSelectors } from '../../../../../state/ducks/documentRevisions/documentType';
import { DocumentType } from '../../../../../state/ducks/documentRevisions/documentType/types';
import AlertDialog from '../../../../app/alert.dialog/AlertDialog';
import { toastError } from '../../../../components/notifications';
import Text from '../../../../components/Text';
import { FBAutocomplete, FBRequiredApprover } from '../../../../form.builder';
import ApprovalApprovedGroup from '../../../../form.builder/FBApprovals/ApprovalApprovedGroup';
import FBMapWrapper from '../../../../form.builder/FBMapWrapper/FBMapWrapper';
import useActionCreator from '../../../../hooks/useActionCreator';
import useAsync from '../../../../hooks/useAsync';
import useDialog, { DialogHandler } from '../../../../hooks/useDialog';
import { withThemeNext } from '../../../../layout/theme-next';
import { enrichConfiguration, evaluateRequirementsConfiguration } from '../../panels/ApprovalConfigurations/helpers';
import { ApprovalConfiguration, ApprovalConfigurationSaveRequest } from '../../panels/ApprovalConfigurations/types';
import { ApprovalConfigurationStage, ApprovalConfigurationStageApprover, APPROVER_TYPE, REQUIRED_APPROVERS } from '../../types';
import ApprovalStageComp from './ApprovalStage';
import useStyles from './dialog.styles';
import { addApproversToStage, compareStageDetails, removeApproverFromStage, removeStage, StageValidationError, updateApproverInStage, updateNameForStage, updateOptionalApproverRejectionChangeForStage, updateRequiredCountForStage, validateStages } from './helpers';
import SaveApprovalConfigurationPopover from './SaveApprovalConfigurationPopover';

export interface ConfigureDialogProps {
  onApprovalStagesUpdated?: (approvalStages?: ApprovalConfigurationStage[]) => void
  configureDialog: DialogHandler
  requiredGroups?: Record<string, FBRequiredApprover>
  configurationId?: string
  approvalRequestId?: string
  documentRevisionId?: string
  isSaveAndAssignVisible?: boolean
  isWithinSettings?: boolean
  currentApprovalStages?: ApprovalConfigurationStage[]
  isGetAllExistingConfigurations?: boolean
  isActionable?: boolean
}

const ApproverConfigurationDialog: React.FunctionComponent<ConfigureDialogProps>
  = ({
    onApprovalStagesUpdated,
    configureDialog,
    requiredGroups = {},
    configurationId,
    approvalRequestId,
    documentRevisionId,
    isWithinSettings = false,
    isSaveAndAssignVisible = true,
    currentApprovalStages = [],
    isGetAllExistingConfigurations = false,
    isActionable = true,
  }) => {
    const classes = useStyles();

    const [stages, setStages] = useState<ApprovalConfigurationStage[]>(currentApprovalStages ?? []);
    const [validationErrors, setValidationErrors] = useState<StageValidationError[]>([]);

    const [saveAnchorEl, setSaveAnchorEl] = useState<HTMLElement | null>(null);
    const handleOpenSavePopover = (event: React.MouseEvent<HTMLElement>) => {
      setSaveAnchorEl(event.currentTarget);
    };

    const switchDialog = useDialog();

    const [availableConfigurations, setAvailableConfigurations] = useState<ApprovalConfiguration[]>([]);
    const [selectedConfigurationId, setSelectedConfigurationId] = useState<string>();

    const [isFetchingConfigDetails, setIsFetchingConfigDetails] = useState<boolean>(false);
    const [isFetchingConfigListCompleted, setIsFetchingConfigListCompleted] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);

    // Fetch details for one configuration
    const fetchApprovalConfigurationAction = useActionCreator(companyActions.fetchApprovalConfiguration);
    const fetchApprovalConfigurationAsync = useAsync({
      onError: (error) => {
        toastError(error);
        setIsFetchingConfigDetails(false);
      },
      onSuccess: (data?: ApprovalConfiguration) => {
        const existingApprovalStages = (data?.approvalStages ?? []);

        // Since within settings, it can be edited multiple times and edits should not be overwritten
        if (!isWithinSettings) {
          setStages(existingApprovalStages);
          if (!existingApprovalStages || existingApprovalStages?.length === 0) {
            addNewStage();
          }
        } else {
          if (currentApprovalStages.length === 0) {
            addNewStage();
          }
        }

        if (data?.name) {
          setSelectedConfigurationId(data?.id);
        }

        if (isWithinSettings && data) {
          setAvailableConfigurations([data]);
        }
        setIsFetchingConfigDetails(false);
      },
    });
    function fetchExistingApprovalConfiguration () {
      setIsFetchingConfigDetails(true);
      const configurationIdToUse = selectedConfigurationId ?? configurationId;
      fetchApprovalConfigurationAsync.start(
        fetchApprovalConfigurationAction,
        { configurationId: configurationIdToUse, documentRevisionId, approvalRequestId },
        fetchApprovalConfigurationAsync,
      );
    }

    // All existing active configurations
    const fetchAllApprovalConfigurationsAction = useActionCreator(companyActions.fetchAllApprovalConfigurations);
    const fetchAllApprovalConfigurationsAsync = useAsync({
      onError: (error) => {
        setIsFetchingConfigListCompleted(true);
        toastError(error);
      },
      onSuccess: (data?: ApprovalConfiguration[]) => {
        const requiredGroupIds = Object.keys(requiredGroups);
        let enrichedConfigs = enrichConfiguration(data ?? [], requiredGroups, allGroups);
        enrichedConfigs = enrichedConfigs.map(enrichedConfig => {
          const coveredRoleIds = new Set(
            enrichedConfig.roleCoverage.coveredRoles.map(role => role.id),
          );
          const coveredCount = requiredGroupIds.filter(id => coveredRoleIds.has(id)).length;
          enrichedConfig.coveredCount = coveredCount;
          return enrichedConfig;
        });
        enrichedConfigs = enrichedConfigs.sort((configA, configB) => (configB.coveredCount ?? 0) - (configA.coveredCount ?? 0));
        setIsFetchingConfigListCompleted(true);
        setAvailableConfigurations(enrichedConfigs);
      },
    });
    function fetchAllApprovalConfigurations (getActiveOnly = true) {
      const options: Record<string, boolean | string> = {
        isActiveOnly: getActiveOnly,
      };
      if (documentRevisionId) {
        options.documentRevisionId = documentRevisionId;
      } else if (approvalRequestId) {
        options.approvalRequestId = approvalRequestId;
      }
      setIsFetchingConfigListCompleted(false);
      fetchAllApprovalConfigurationsAsync.start(
        fetchAllApprovalConfigurationsAction,
        options,
        fetchAllApprovalConfigurationsAsync,
      );
    }

    // Create a new configuration from within an entity
    const createConfigurationAction = useActionCreator(companyActions.createApprovalConfiguration);
    const createConfigurationAsync = useAsync({
      onError: (error) => {
        toastError(error);
        setIsSaving(false);
      },
      onSuccess: (data) => {
        if (onApprovalStagesUpdated) {
          onApprovalStagesUpdated();
        }
        setIsSaving(false);
        resetAndCloseDialog();
      },
    });
    function createConfiguration (body: ApprovalConfigurationSaveRequest) {
      createConfigurationAsync.start(
        createConfigurationAction,
        body,
        createConfigurationAsync,
      );
    }

    useEffect(() => {
      if (configurationId) {
        setSelectedConfigurationId(configurationId);
      }
      if (configurationId || approvalRequestId || documentRevisionId) {
        fetchExistingApprovalConfiguration();
      } else {
        if (!currentApprovalStages.length) {
          addNewStage();
        }
      }
    }, [configurationId, approvalRequestId, documentRevisionId]);

    useEffect(() => {
      if (isGetAllExistingConfigurations) {
        fetchAllApprovalConfigurations();
      }
    }, [isGetAllExistingConfigurations]);

    const handleCloseSavePopover = () => {
      setSaveAnchorEl(null);
    };

    useEffect(() => {
      if (!validationErrors.length) {
        return;
      }

      const timer = setTimeout(() => {
        setValidationErrors([]);
      }, 5000);

      return () => clearTimeout(timer);
    }, [validationErrors]);

    const allGroups: Group[] = useSelector(authSelectors.currentUserGroups);
    const groupOptions = allGroups.map((group) => ({
      id: group.id,
      name: group.name,
      joinedEmployees: group.joinedEmployees,
    }));

    const employeeGroupMap = new Map<string, string[]>();
    Object.values(requiredGroups).forEach(group => {
      group.joinedEmployees.forEach(employeeId => {
        const existing = employeeGroupMap.get(employeeId) ?? [];
        existing.push(group.name);
        employeeGroupMap.set(
          employeeId,
          existing,
        );
      });
    });

    const allEmployees: Employee[] = useSelector(companySelectors.getActiveEmployees) as Employee[];
    const employeeOptions = allEmployees.map(
      employee => ({
        id: employee.id,
        name: employee.user?.name,
        email: employee.user?.email,
        satisfyingGroups: employeeGroupMap.get(employee.id) ?? [],
      }),
    );
    const employeeOptionsMap = { ...(keyBy(employeeOptions, (employee) => employee.id)) };

    // Document Types
    const allDocumentTypes: DocumentType[] = useSelector(documentTypeSelectors.getDocumentTypeList);
    const activeDocumentTypes = allDocumentTypes.filter(documentType => documentType.active);
    const documentTypeOptions = activeDocumentTypes.map(documentType => ({
      id: documentType.id,
      name: documentType.documentTypeName,
    }));
    const documentTypeOptionsMap = { ...(keyBy(documentTypeOptions, (group) => group.id)) };

    const checkedGroups = useMemo(
      () => evaluateRequirementsConfiguration(stages, requiredGroups, allGroups),
      [stages, requiredGroups, allGroups],
    );

    const addNewStage = (): void => {
      if (stages.length >= 10) {
        toastError(translate('approvers.configure.approval.stage.length.error.msg'));
        return;
      }

      const stageNumber = stages.length + 1;
      const newStage = {
        requiredCount: REQUIRED_APPROVERS.ALL,
        assignedApprovers: [],
        stageNumber,
        name: `Stage ${stageNumber}`,
        optionalApproverRejectionRejectsStage: false,
      };
      setStages(prev => [...prev, newStage]);
    };

    const handleAddApprover = (stageNumber: number, approverId: string): void => {
      if (approverId) {
        const approver: ApprovalConfigurationStageApprover = {
          id: approverId,
          name: employeeOptionsMap[approverId].name,
          type: APPROVER_TYPE.USER,
          isOptional: false,
        };
        const updatedStages = addApproversToStage(stages, stageNumber, [approver]);
        setStages(updatedStages);
      }
    };

    const handleRemoveApprover = (stageNumber: number, approverIndex: number): void => {
      const updatedStages = removeApproverFromStage(stages, stageNumber, approverIndex);
      setStages(updatedStages);
    };

    const updateApproverDetails = (stageNumber: number, approverId: string, updates: Partial<ApprovalConfigurationStageApprover>): void => {
      const updatedStages = updateApproverInStage(stages, stageNumber, approverId, updates);
      setStages(updatedStages);
    };

    const updateRequiredCount = (stageNumber: number, requiredCount: REQUIRED_APPROVERS): void => {
      const updatedStages = updateRequiredCountForStage(stages, stageNumber, requiredCount);
      setStages(updatedStages);
    };

    const updateStageName = (stageNumber: number, name: string): void => {
      const updatedStages = updateNameForStage(stages, stageNumber, name);
      setStages(updatedStages);
    };

    const updateOptionalApproverRejectionChange = (stageNumber: number, value: boolean): void => {
      const updatedStages = updateOptionalApproverRejectionChangeForStage(stages, stageNumber, value);
      setStages(updatedStages);
    };

    const deleteStage = (stageNumber: number): void => {
      const updatedStages = removeStage(stages, stageNumber);
      setStages(updatedStages);
    };

    const handleAddGroup = (stageNumber: number, groupId: string) => {
      const group = find(allGroups, {
        id: groupId,
      });

      if (!group) {
        return;
      }

      const approver: ApprovalConfigurationStageApprover = {
        id: groupId,
        name: group.name,
        type: APPROVER_TYPE.GROUP,
        employeeCount: group.joinedEmployees.length,
        isOptional: false,
      };
      const updatedStages = addApproversToStage(stages, stageNumber, [approver]);
      setStages(updatedStages);
    };

    const onConfigurationSelected = (configurationId: string) => {
      setSelectedConfigurationId(configurationId);
      if (stages.length || selectedConfigurationId) {
        switchDialog.open();
      } else {
        fetchExistingApprovalConfiguration();
      }
    };

    const switchToConfigurationConfirmed = () => {
      fetchExistingApprovalConfiguration();
      switchDialog.close();
    };

    const getCoverageIndicator = (requiredCount: number, coveredCount: number | undefined = 0) => {
      if (coveredCount === requiredCount) {
        return <FontAwesomeIcon icon={solid('check-circle')} className={classes.check} />;
      }

      if (coveredCount === 0) {
        return <FontAwesomeIcon icon={solid('circle-xmark')} className={classes.notFulfilled} />;
      }

      if (coveredCount < requiredCount) {
        return <FontAwesomeIcon icon={solid('warning')} className={classes.partiallyFulfilled} />;
      }
    };

    const renderConfiguration = (option: ApprovalConfiguration) => {
      const requiredGroupIds = Object.keys(requiredGroups);
      return (
        <Box>
          <Box className={classes.configurationOption}>
            <Box className={classes.genericFlex}>
              {getCoverageIndicator(requiredGroupIds.length, option.coveredCount)}

              <Typography variant="body1" className={classes.optionName}>
                {option.name}
              </Typography>
            </Box>

            <Box className={classes.chipOptions}>
              {option.documentTypeIds?.map(docTypeId => {
                const docTypeName = documentTypeOptionsMap[docTypeId]?.name;
                if (docTypeName) {
                  return <Chip
                    key={docTypeId}
                    size="small"
                    label={docTypeName}
                    className={classes.chip}
                  />;
                } else {
                  return <></>;
                }
              })}
            </Box>
          </Box>

          <Typography variant="subtitle2" noWrap className={classes.configurationDetails}>
            {option.description}
          </Typography>
        </Box>
      );
    };

    const handleSaveConfiguration = (name?: string, description?: string) => {
      // Stages length should at least be 1.
      if (stages.length < 1) {
        toastError('At least one stage is required');
        return;
      }
      setIsSaving(true);

      // Each Stage should have at least one required approver and should match with the requiredCount
      const stageValidationErrors = validateStages(stages);
      setValidationErrors(stageValidationErrors);

      if (stageValidationErrors.length > 0) {
        setIsSaving(false);
        return;
      }

      // compare the current approval stages and the approval stages of the selectedConfiguration if any.
      let isStagesUpdated = false;
      let isUnassign = false;
      let configurationIdToUse = selectedConfigurationId;

      // Within Settings a new configuration is being added
      if (isWithinSettings && !availableConfigurations.length) {
        isStagesUpdated = true;
      } else if (selectedConfigurationId) { // settings edit Or Entity connected configuration
        const selectedConfiguration
          = availableConfigurations.find(configuration => configuration.id === selectedConfigurationId);
        if (selectedConfiguration) {
          const comparison = compareStageDetails(stages, selectedConfiguration.approvalStages);
          if (comparison.hasChanges) {
            configurationIdToUse = undefined;
            isUnassign = true;
            isStagesUpdated = true;
          }
        }
      } else if (!isWithinSettings && !selectedConfigurationId) { // Entity separate
        isStagesUpdated = true;
      }

      if (!isWithinSettings) {
        createConfiguration({
          approvalRequestId,
          documentRevisionId,
          approvalStages: stages,
          name,
          description,
          id: configurationIdToUse,
          isUnassign,
        });
        return;
      }

      if (onApprovalStagesUpdated && isStagesUpdated) {
        onApprovalStagesUpdated(stages);
      }

      resetAndCloseDialog();
    };

    const resetAndCloseDialog = () => {
      setSelectedConfigurationId(undefined);
      setStages([]);
      configureDialog.close();
    };

    const getConfirmButtonTranslation = () => {
      if (isSaving) {
        return isWithinSettings
          ? 'approvers.configure.saving.btn'
          : 'approvers.configure.assigning.btn';
      }

      return isWithinSettings
        ? 'approvers.configure.save.btn'
        : 'approvers.configure.assign.btn';
    };

    const sortedStages = useMemo(() =>
      [...stages].sort((a, b) => a.stageNumber - b.stageNumber),
    [stages]);

    return (
      <Dialog
        disableEscapeKeyDown={true}
        disableBackdropClick={true}
        open={configureDialog.isOpen}
        maxWidth="xl"
        classes={{
          root: classes.root,
          paper: classes.paper,
        }}
      >
        <DialogTitle>
          <Grid container spacing={3}>
            <Grid item xs={3}>
              <Typography className={classes.dialogHeader}>
                <Text translation="approvers.configure.title" />
              </Typography>
            </Grid>
            {Object.values(requiredGroups).length
              ? <Grid item xs={4}>
                <fieldset className={classes.requiredGroupsContainer}>
                  <legend className={classes.requiredGroupsLegend}>
                    <Text translation="approvers.configure.required.legend" />
                  </legend>
                  <Box display="flex" flexWrap="wrap" mt={1}>
                    <FBMapWrapper<Record<string, FBRequiredApprover>> collection={requiredGroups}>
                      {(group: FBRequiredApprover, id) => (
                        <Box className={classes.approverGroup} key={`required-group-${id}`}>
                          <ApprovalApprovedGroup
                            groupName={group.name}
                            key={id}
                            isChecked={checkedGroups.findIndex(group => group.id === id) > -1}
                            dataCyPrefix="required-group"
                          />
                        </Box>
                      )}
                    </FBMapWrapper>
                  </Box>
                </fieldset>
              </Grid> : <></>}
            {isGetAllExistingConfigurations && isFetchingConfigListCompleted && (
              availableConfigurations.length > 0 ? (
                <Grid item xs={5}>
                  <FBAutocomplete
                    placeholder="approver.configure.configuration.selector.autocomplete.placeholder"
                    options={availableConfigurations}
                    optionLabelKey="name"
                    optionValueKey="id"
                    gutter={false}
                    omitFormValue={true}
                    name="configuration-selector"
                    onChange={(e, value) => {
                      onConfigurationSelected(value?.id);
                    }}
                    disabled={!isActionable}
                    renderOption={renderConfiguration}
                    defaultValue={selectedConfigurationId}
                    groupByKey="N/A"
                  />
                </Grid>
              ) : (
                documentRevisionId && (
                  <Grid item xs={5}>
                    <Typography variant="subtitle2" className={classes.noConfigurationMsg}>
                      {translate('approval.configuration.selector.no.configurations')}
                    </Typography>
                  </Grid>
                )
              )
            )}
          </Grid>
        </DialogTitle>
        <DialogContent className={classes.content}>
          <div className={classes.fullWidth}>
            <Box className={classes.stagesContainer}>
              { isFetchingConfigDetails && (
                <Box className={classes.loadingContainer}>
                  <Typography variant="h2" color="secondary">
                    <HourglassFullIcon /> {translate('common.loading')}
                  </Typography>
                </Box>
              )}
              {
                !isFetchingConfigDetails && sortedStages.map(stage => (
                  <ApprovalStageComp
                    key={stage.stageNumber}
                    stage={stage}
                    isActionable={isActionable}
                    onAddApprover={handleAddApprover}
                    onRemoveApprover={handleRemoveApprover}
                    onRequiredCountChange={updateRequiredCount}
                    onAddGroup={handleAddGroup}
                    onHandleApproverDetailsUpdated={updateApproverDetails}
                    onOptionalApproverRejectionChange={updateOptionalApproverRejectionChange}
                    onStageNameChange={updateStageName}
                    onDelete={deleteStage}
                    allApprovers={employeeOptions}
                    allGroups={groupOptions}
                    validationErrors={
                      validationErrors.filter(validationError => validationError.stageNumber === stage.stageNumber)}
                  />
                ))
              }
            </Box>
          </div>
          <Box className={classes.actionBtns}>
            { isActionable && <StyledButton
              id="addStage"
              data-cy="add-stage"
              fullWidth={false}
              className={classes.addStage}
              onClick={addNewStage}
            >
              <Text translation="approvers.configure.add.stage.btn" />
            </StyledButton> }
            <Box className={classes.btnGroup}>
              <Button
                variant="text"
                data-cy="cancel"
                onClick={configureDialog.close}
              >
                <Text translation="common.cancel" />
              </Button>
              { isActionable && <Button
                id="confirm"
                data-cy="confirm"
                color="primary"
                fullWidth={false}
                disabled={isSaving}
                onClick={() => handleSaveConfiguration()}
              >
                <Text translation={getConfirmButtonTranslation()} />
              </Button> }
              { isSaveAndAssignVisible && isActionable && <Button
                id="saveAndConfirm"
                data-cy="save-and-confirm"
                color="secondary"
                fullWidth={false}
                onClick={handleOpenSavePopover}
              >
                <Text translation="approvers.configure.save.and.assign.btn" />
              </Button> }
            </Box>
          </Box>
        </DialogContent>
        <SaveApprovalConfigurationPopover
          anchorEl={saveAnchorEl}
          onClose={handleCloseSavePopover}
          onSave={(name, description) => {
            handleSaveConfiguration(name, description);
          }}
        />
        <AlertDialog
          confirmAction={switchToConfigurationConfirmed}
          dialog={switchDialog}
          message="approvers.configure.switch.configuration.alert"
        />
      </Dialog>
    );
  };

export default withThemeNext(ApproverConfigurationDialog);
